import React, { useState, useMemo } from 'react';
import { Search, Calendar, Trash2, Edit } from 'lucide-react';
import { DataTable, Column } from '../common/DataTable';
import { useSalesHistory, useDeleteSale } from '../../hooks/useSales';
import { Sale, SaleFormData } from '../../types/Sales';
import { formatDateTime } from '../../utils/formatters';
import { SalesModal } from '../sales/SalesModal';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Toast } from '../common/Toast';
import { useProducts } from '../../hooks/useProducts';
import { getErrorMessage } from '../../utils/errorMessages';

// Helper function to generate unique numeric ID from string
const generateId = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

export const FinancialAnalysisTab: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterType, setFilterType] = useState<'profit' | 'quantity'>('profit');
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [localSales, setLocalSales] = useState<Sale[]>([]);

  const searchParams = useMemo(() => {
    if (!startDate || !endDate) return null;
    return { startDate, endDate };
  }, [startDate, endDate]);

  const { data: history = [], isLoading } = useSalesHistory(
    searchParams || { startDate: '', endDate: '' }
  );
  const { data: productsPage } = useProducts({ page: 0, size: 1000 });
  const deleteSale = useDeleteSale();
  
  const products = useMemo(() => productsPage?.content || [], [productsPage?.content]);

  // Convert SalesHistoryItem to Sale and merge with local sales
  const salesList = useMemo(() => {
    if (!searchParams) return [];
    
    const historySales: Sale[] = history.map((item, index) => {
      const product = products.find(p => p.name === item.productName);
      const sellingPrice = product?.retailPrice || 0; // Use retail price for sales
      const uniqueIdStr = `history-${item.saleDate}-${index}`;
      return {
        id: generateId(uniqueIdStr),
        productId: product?.id || 0,
        productName: item.productName,
        productBrand: item.productBrand,
        quantitySold: item.quantitySold,
        totalEarning: item.quantitySold * sellingPrice,
        saleDate: item.saleDate,
      };
    });
    
    // Merge with local sales (updated/deleted sales are filtered out)
    const historyIds = new Set(historySales.map(s => s.id));
    const localOnly = localSales.filter(s => !historyIds.has(s.id));
    return [...historySales, ...localOnly];
  }, [history, products, localSales, searchParams]);

  // Filter and sort based on filterType
  const filteredSales = useMemo(() => {
    let filtered = [...salesList];
    
    if (filterType === 'profit') {
      filtered.sort((a, b) => b.totalEarning - a.totalEarning);
    } else {
      filtered.sort((a, b) => b.quantitySold - a.quantitySold);
    }
    
    return filtered;
  }, [salesList, filterType]);

  const handleSearch = () => {
    if (!startDate || !endDate) {
      return;
    }
    setSelectedSale(null); // Reset selection on new search
  };

  const handleRowClick = (sale: Sale) => {
    setSelectedSale(sale);
  };

  const handleEdit = (sale: Sale) => {
    setSelectedSale(sale);
    setModalOpen(true);
  };

  const handleDeleteClick = () => {
    if (selectedSale) {
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedSale) {
      try {
        await deleteSale.mutateAsync(selectedSale.id);
        // Also remove from local sales
        setLocalSales(prev => prev.filter(s => s.id !== selectedSale.id));
        setToast({ 
          open: true, 
          message: 'Satış başarıyla silindi ve stok geri eklendi', 
          severity: 'success' 
        });
        setDeleteDialogOpen(false);
        setSelectedSale(null);
      } catch (error: any) {
        setToast({ 
          open: true, 
          message: getErrorMessage(error, 'Satış silinirken hata oluştu'), 
          severity: 'error' 
        });
      }
    }
  };

  const handleSave = (data: SaleFormData) => {
    if (selectedSale) {
      // Frontend-only: Update local sales
      const updatedSale: Sale = {
        ...selectedSale,
        ...data,
        productName: products.find(p => p.id === data.productId)?.name || selectedSale.productName,
        productBrand: products.find(p => p.id === data.productId)?.brand || selectedSale.productBrand,
      };
      setLocalSales(prev => {
        const filtered = prev.filter(s => s.id !== selectedSale.id);
        return [...filtered, updatedSale];
      });
      setToast({ open: true, message: 'Satış başarıyla güncellendi', severity: 'success' });
    }
    setModalOpen(false);
    setSelectedSale(null);
  };

  const columns: Column<Sale>[] = [
    { id: 'productName', label: 'Ürün Adı', minWidth: 150 },
    { id: 'productBrand', label: 'Marka', minWidth: 100 },
    {
      id: 'saleDate',
      label: 'Satış Tarihi',
      minWidth: 150,
      format: (value) => value ? formatDateTime(value as string) : '-',
    },
    { id: 'quantitySold', label: 'Miktar', minWidth: 80, align: 'right' },
    {
      id: 'totalEarning',
      label: 'Toplam Tutar',
      minWidth: 120,
      align: 'right',
      format: (value) => `${(value as number).toFixed(2)} ₺`,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header with Action Buttons */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Finansal Analiz</h3>
        <div className="flex items-center gap-3">
          {selectedSale && (
            <>
              <button
                onClick={() => handleEdit(selectedSale)}
                className="btn-secondary flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Düzenle
              </button>
              <button
                onClick={handleDeleteClick}
                className="btn-danger flex items-center gap-2"
                disabled={!selectedSale}
              >
                <Trash2 className="w-4 h-4" />
                Sil
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-foreground-secondary mb-2 flex items-center gap-2 h-6">
              <Calendar className="w-4 h-4 text-foreground-tertiary" />
              Başlangıç Tarihi
            </label>
            <div className="relative flex-1">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input pr-10 h-9 md:h-10 w-full"
              />
            </div>
          </div>
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-foreground-secondary mb-2 flex items-center gap-2 h-6">
              <Calendar className="w-4 h-4 text-foreground-tertiary" />
              Bitiş Tarihi
            </label>
            <div className="relative flex-1">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input pr-10 h-9 md:h-10 w-full"
              />
            </div>
          </div>
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-foreground-secondary mb-2 h-6">
              Filtre
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as 'profit' | 'quantity')}
              className="input h-10 w-full"
            >
              <option value="profit">Kâr Bazlı Analiz</option>
              <option value="quantity">Miktar Bazlı Analiz</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="block text-sm font-medium text-foreground-secondary mb-2 h-6 invisible">
              Ara
            </label>
            <button
              onClick={handleSearch}
              className="btn-primary w-full flex items-center justify-center gap-2 h-10"
            >
              <Search className="w-4 h-4" />
              Ara
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {searchParams ? (
        <DataTable
          columns={columns}
          rows={filteredSales}
          loading={isLoading}
          onRowClick={handleRowClick}
          onRowDoubleClick={handleEdit}
          getRowId={(row: Sale) => row.id}
          selectedRowId={selectedSale?.id}
        />
      ) : (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-background-tertiary flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-foreground-tertiary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            Finansal Analiz
          </h3>
          <p className="text-sm text-foreground-secondary">
            Analiz için başlangıç ve bitiş tarihlerini seçin ve ara butonuna tıklayın.
          </p>
        </div>
      )}

      {/* Modals */}
      {selectedSale && (
        <SalesModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedSale(null);
          }}
          onSave={handleSave}
          sale={selectedSale}
          products={products}
          loading={false}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedSale(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Satış Sil"
        message={`Bu satışı silmek istediğinize emin misiniz? Satılan ürünlerin stoğu geri eklenecektir.`}
        loading={deleteSale.isPending}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast({ ...toast, open: false })}
        message={toast.message}
        severity={toast.severity}
      />
    </div>
  );
};
